import _ from 'underscore';
import React from 'react';
import PropTypes from 'prop-types';
import { jss } from 'react-jss';
import { App } from '@bingads-webui-react/app';
import { Router, withRouter } from '@bingads-webui-campaign-react/router';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import { LayoutContainer, Layout, OverlayComponent } from '@bingads-webui-react/app-layout-container';
import { UserContext } from '@bingads-webui-react/user-context';
import { SettingsContext } from '@bingads-webui-react/settings-context';
import { PopupContextProvider } from '@bingads-webui-react/popup-manager';
import { MultiStepTeachingBubbleContextProvider } from '@bingads-webui-campaign-react/multi-step-teaching-bubble';
import { Observable } from '@bingads-webui-universal/observable';
import { GlobalFilterContextProvider } from '@bingads-webui-campaign-react/global-filter-context';
import { GlobalContextReceiver } from '@bingads-webui-campaign/global-context-store';
import { withSiteMap, processor, navRefreshSiteMap } from '@bingads-webui-campaign-react/with-site-map';
import { processorSet } from '@bingads-webui-react/with-site-map';
import { withPageReadyAsync } from '@bingads-webui-react/with-page-ready';
import { perfMarkerPropTypes } from '@bingads-webui/performance-marker';
import { createGenerateId } from '@bingads-webui-campaign-react/custom-jss-config';
import {
  CampaignMainMenuPlaceholder,
  CampaignPageMenuPlaceholder,
  SummaryBarPlaceholder,
  GlobalMenueWrapperPlaceholder,
  NewGlobalMenueWrapperPlaceholder,
  PrimaryNavigationPlaceholder,
  SecondaryNavigationPlaceholder,
  ScopePanelPlaceholder,
} from '@bingads-webui-campaign-react/shell-ui-common';
import { isSDK } from '@bingads-webui-campaign-react/sdk-utils';
import { getDisplayMode, DisplayMode } from '@bingads-webui-campaign-react/display-mode';
import { WebViewContextProvider } from '@bingads-webui-campaign-react/webview-context';
import { ServerContext, withServerContext } from '@bingads-webui-campaign-react/server-context';
import { withPreference } from '@bingads-webui-react/preference-context';
import { I18nModel } from '@bingads-webui-universal/i18n-model';
import { getGlobalErrorOptions, withComponentErrorLogging } from '@bingads-webui-campaign-react/global-error-options';
import { isNavigationRefreshOn } from '@bingads-webui-react/preference-utility';
import { AdsCopilotContextProvider } from '@bingads-webui-react/msa-copilot-common';
import { ToastProvider } from '@bingads-webui-fluent-component/toast-provider';
import { CampaignFetchProvider } from '@bingads-webui-campaign-react/campaign-http';

import AppContent from './components/app-content';
import { CampaignTypeUpdater } from './components/campaign-type-filter-updater';
import { FallbackUpdaterWithDefaultLandingPage } from './components/fallback-updater';
import { AppPerfMarker, withAppPerfMarker } from './contexts/app-perfmarker-context';
import { ScopeProvider } from './components/scope-provider';
import { ScopeSwitchingReloader } from './components/scope-switching-reloader';
import { NewVersionReloader } from './components/new-version-reloader';
import { initOdataCache } from './global-init/init-odata-cache';
import { initSettingsContext } from './global-init/init-settings-context';
import i18n from './main.l10n';
import { withDFMProvider } from './data-fetch/with-dfm';
import { themes } from './themes-config';
import { configs as teachingBubbleConfigs } from './components/teaching-bubble/teaching-bubble-config';
import { LazySDKAppWrapper } from './sdk/dynamic-routes';
import { setTestContext } from './test-context-side-effect';
import { LayoutSwitch } from './components/layout-switch';

require.context('@bingads-webui/theme-2018/dist/static/media');
require.context('@bingads-webui-react/feature-adoption-coupon/dist/static/media');
require.context('@bingads-webui-react/rich-text-editor/dist/static/media');

const isSDKView = isSDK();
const displayMode = getDisplayMode();
const appName = new URLSearchParams(document.location.search).get('applicationName');
const shouldShowFallbackUpdater = appName ? !appName.includes('BingAdsEditor') : true;
const LayoutContainerWithRouter = withDFMProvider(withRouter(LayoutContainer));
const WrappedGlobalContextReceiver = withSiteMap(withServerContext(GlobalContextReceiver), [processor.processPermissions, processor.processPath]);
const navRefreshProcessorSet = [
  processor.processImportCenterMode,
  processor.processMixedModeAdGroup,
  processor.processGlobalFilter,
  processor.processCampaignType,
  processor.processCampaignId,
  processor.processSmartCampaign,
  processor.processSmartShoppingCampaign,
  processor.processHotelCampaign,
  processor.processPmaxCampaignPageMenu,
  processor.processPmaxRetailCampaignPageMenu,
  processor.processCcuiContext,
  processor.processMMCMenu,
  processor.processCopilotRegionRestriction,
  ...processorSet.navRefresh,
];

const DRAFT_ACCOUNT_STATUS = 30;

const getDelayedTopBannerComponent = ({
  getTopBannerLayout,
  getStoreInstance,
  DisapprovedEntities,
  ImportStatus,
  NoEntityNotifications,
  PredefinedNotifications,
  UETNotifications,
  CCNotifications,
  ImportNotification,
  DraftStoreNotification,
  PmaxCampaignUpgradeNotification,
  LocationIntentUpdateNotifications,
  HotelCenterMigrationNotification,
  HotelCenterMigrationPhase2Notification,
  HotelCenterMigrationPhase3Notification,
  BroadOnlyNotification,
  GMCTokenExpiredNotification,
  DeprecateSyndicationSearchOnlyNotification,
}) => {
  const getNotifications = context => [
    new PredefinedNotifications(context).getPredefinedAlerts(),
    new DisapprovedEntities(context).getDisapprovedEntityNotifications(),
    new ImportStatus(context).getImportStatusNotifications(),
    new NoEntityNotifications(context).getNoEligibleEntityNotifications(),
    new UETNotifications(context).getUETNotifications(),
    new CCNotifications(context).getCCNotifications(),
    new ImportNotification(context).getImportNotifications(),
    new DraftStoreNotification(context).getDraftStoreNotifications(),
    new PmaxCampaignUpgradeNotification(context).getPmaxCampaignUpgradeNotifications(),
    new LocationIntentUpdateNotifications(context).getLocationIntentUpdateNotifications(),
    new HotelCenterMigrationPhase3Notification(context).getHotelCenterMigrationPhase3Notifications(),
    new HotelCenterMigrationPhase2Notification(context).getHotelCenterMigrationPhase2Notifications(),
    new HotelCenterMigrationNotification(context).getHotelCenterMigrationNotifications(),
    new BroadOnlyNotification(context).getBroadOnlyNotifications(),
    new GMCTokenExpiredNotification(context).getAlerts(),
    new DeprecateSyndicationSearchOnlyNotification(context).getDeprecateSyndicationSearchOnlyNotifications(),
  ];

  const store = getStoreInstance({ getNotifications });
  store.refresh();

  return {
    default: getTopBannerLayout(store),
  };
};

const DelayedTopBanner = withPageReadyAsync(() =>
  import(/* webpackMode: 'eager' */'@bingads-webui-campaign/notification-center')
    .then(getDelayedTopBannerComponent));

const DelayedMainMenu = withPageReadyAsync(() =>
  import(/* webpackMode: 'eager' */'@bingads-webui-campaign-react/main-menu-container')
    .then(({ MainMenuContainer }) => ({
      default: MainMenuContainer,
    })), CampaignMainMenuPlaceholder);

const DelayedAfterPageReady = withPageReadyAsync(() =>
  import(/* webpackMode: 'eager' */'./components/after-page-ready')
    .then(({ AfterPageReady }) => ({
      default: AfterPageReady,
    })));

const DelayedSummaryBarContainer = withPageReadyAsync(() =>
  import(/* webpackMode: 'eager' */'@bingads-webui-campaign-react/summary-bar-container')
    .then(({ SummaryBarContainer }) => ({
      default: SummaryBarContainer,
    })), SummaryBarPlaceholder);

const DelayedPageMenuContainer = withPageReadyAsync(() =>
  import(/* webpackMode: 'eager' */'@bingads-webui-campaign-react/page-menu-container')
    .then(({ PageMenuContainer }) => ({
      default: PageMenuContainer,
    })), CampaignPageMenuPlaceholder);

const DelayedSubTabContainer = withPageReadyAsync(() =>
  import(/* webpackMode: 'eager' */'@bingads-webui-campaign-react/sub-tab-container')
    .then(({ SubTabContainer }) => ({
      default: SubTabContainer,
    })), null);

const DelayedSubpageMenuContainer = withPageReadyAsync(() =>
  import(/* webpackMode: 'eager' */'@bingads-webui-campaign-react/sub-page-menu-container')
    .then(({ SubpageMenuContainer }) => ({
      default: withPreference(withServerContext(SubpageMenuContainer)),
    })), null);

const LazyDraft = React.lazy(() =>
  import(/* webpackMode: 'eager' */'./components/draft-error-page')
    .then(({ DraftAccountPageWraper }) => ({
      default: DraftAccountPageWraper,
    })));

const DelayedDraftAccountPage = props => (
  <React.Suspense fallback={null}>
    <LazyDraft {...props} />
  </React.Suspense>
);

const DelayedNewGlobalMenuWrapper = withPageReadyAsync(() =>
  import(/* webpackMode: 'eager' */'./components/new-global-menu-wrapper')
    .then(({ NewGlobalMenuWrapper }) => ({
      default: withComponentErrorLogging(NewGlobalMenuWrapper, 'NavigationRefreshGlobalMenu'),
    })), NewGlobalMenueWrapperPlaceholder);

const DelayedMSAdsCopilotLoader = withPageReadyAsync(() => import(/* webpackMode: 'eager' */ './components/ads-copilot/loader'));

const DelayedTeachingBubbleRenderer = withPageReadyAsync(() =>
  import(/* webpackMode: 'eager' */'./components/teaching-bubble/teaching-bubble-renderer')
    .then(({ TeachingBubbleRenderer }) => ({
      default: TeachingBubbleRenderer,
    })));

jss.setup({
  createGenerateId,
});

class AppRoot extends React.PureComponent {
  constructor(props) {
    super(props);
    const { serverContext, instrumentation, appPerfMarker } = props;

    // the real user context should only contains the user information
    this.userContext = {
      CurrentUser: serverContext.CurrentUser,
      CurrentCustomer: serverContext.CurrentCustomer,
      CurrentAccount: serverContext.CurrentAccount,
    };
    this.isAccountDraft = serverContext.CurrentAccount && serverContext.CurrentAccount.Status === DRAFT_ACCOUNT_STATUS;

    this.globalErrorOptions = getGlobalErrorOptions({
      instrumentation,
    });

    this.defaultScope = {
      uid: serverContext.CurrentUser.Id,
      cid: serverContext.CurrentCustomer.Id,
      aid: serverContext.CurrentAccount.Id,
    };

    this.permissions = {
      ...serverContext.Permissions,
      dynamic: serverContext.DynamicPermissions || {},
    };

    this.settingsContext = initSettingsContext(serverContext, this.permissions);

    // TODO - Temporary marking it done here to verify perf markers and instrumentation in New UI.
    const perfLatencies = window.performance ? { SchemaNewLatency: window.performance.SchemaNewLatency, siteMapLatency: window.performance.siteMapLatency } : null;
    appPerfMarker.done(perfLatencies);
    this.isProdEnv = serverContext.IsProdEnv;

    // warm up client side odata cache with server prefetched odata response
    initOdataCache(serverContext);
    let onToggleMainMenu = () => { };
    this.toggleMainMenuObservable = new Observable((observer) => {
      onToggleMainMenu = () => observer.next();
    });
    this.onHamburgerClicked = () => { onToggleMainMenu(); };

    // logger for popup management
    this.logger = instrumentation.popupManagement.create().popupManager.create();
    this.teachingBubbleLogger = instrumentation.popupManagement.create().multiStepTeachingBubble.create();
  }

  setupI18n() {
    const { serverContext } = this.props;

    const defaultFailFast = this.isProdEnv ? 'SAFE' : i18n.failFast;

    i18n.failFast = defaultFailFast;
    I18nModel.setDefaultFailFast(defaultFailFast);

    i18n.currency = serverContext.CurrentAccount.CurrencyCode;

    // This is used for private\ui-next\packages\ui-next\src\legacy-dep-bootstrap\require.config.js
    window.globalLegacyI18n.main = i18n;
  }

  render() {
    // Because the messy of dependency graph and side effect, we could not simply introduce the test related side effect at the very first of this bootstrap.js.
    // For now, it have to be imported after './legacy-dep-bootstrap/init-legacy-components', to wait for instrumentation setup
    // And, we have to set test context everytime when rendering, rather than only setting it once. Because after switching account, window.xxx seems
    // to be cleared
    // if (window.location.search && window.location.search.includes('E2ETestInjectContext')) {
    setTestContext();
    // }

    this.setupI18n();

    const DraftAccountPage = this.isAccountDraft ? DelayedDraftAccountPage : React.Fragment;

    const DelayedGlobalMenuWrapper = this.props.serverContext.DynamicPermissions.DelayGlobalMenu
      ? withPageReadyAsync(() =>
        import(/* webpackMode: 'eager' */'./components/global-menu-wrapper')
          .then(({ GlobalMenuWrapper }) => ({
            default: GlobalMenuWrapper,
          })), GlobalMenueWrapperPlaceholder)
      : React.lazy(() => import(/* webpackMode: 'eager' */ './components/global-menu-wrapper')
        .then(({ GlobalMenuWrapper }) => ({
          default: GlobalMenuWrapper,
        })));

    const isNavRefresh = isNavigationRefreshOn(this.permissions);

    const DelayedPrimaryNavigation = isNavRefresh && withPageReadyAsync(
      () => import(/* webpackMode: 'eager' */'@bingads-webui-react/primary-navigation')
        .then(({ PrimaryNavigation }) => ({
          default: withComponentErrorLogging(withSiteMap(PrimaryNavigation, navRefreshProcessorSet, navRefreshSiteMap), 'NavigationRefreshPrimaryNavigation'),
        })),
      PrimaryNavigationPlaceholder
    );

    const DelayedSecondaryNavigation = isNavRefresh && withPageReadyAsync(
      () => import(/* webpackMode: 'eager' */'@bingads-webui-campaign-react/secondary-navigation')
        .then(({ SecondaryNavigation }) => ({
          default: withComponentErrorLogging(withSiteMap(SecondaryNavigation, navRefreshProcessorSet, navRefreshSiteMap), 'NavigationRefreshSecondaryNavigation'),
        })),
      SecondaryNavigationPlaceholder
    );

    const DelayedScopePanel = isNavRefresh && withPageReadyAsync(
      () => import(/* webpackMode: 'eager' */'@bingads-webui-campaign-react/scope-panel')
        .then(({ ScopePanel }) => ({
          default: withComponentErrorLogging(ScopePanel, 'NavigationRefreshScopePanel'),
        })),
      ScopePanelPlaceholder
    );

    const isCouponTeachingBubbleEnabled = _.result(this.props.serverContext, ['DynamicPermissions', 'CouponTeachingBubble'], false);

    return (
      <App
        globalErrorOptions={this.globalErrorOptions}
        i18nModel={i18n}
        instrumentation={this.props.instrumentation}
        theme={themes[this.props.curTheme]}
        preferenceService={this.props.preferenceService}
      >
        <div className="app-shell" >
          <AppPerfMarker.Provider value={this.props.appPerfMarker}>
            <GlobalFilterContextProvider>
              <Router>
                <PermissionsContext.Provider value={this.permissions}>
                  <ScopeProvider
                    defaultScope={this.defaultScope}
                    allowedKeys={[
                      'uid',
                      'cid',
                      'aid',
                      'campaignId',
                      'adGroupId',
                      'adId',
                      'ptId',
                      'instanceId',
                      'feedId',
                      'budgetId',
                      'selectedKeywords',
                      'recommendationType',
                      'selListId',
                      'bulkEditSessionId',
                      'executionId',
                      'importId',
                      'taskItemId',
                      'tagId',
                      'scriptId',
                      'logId',
                      'type',
                      'storeId',
                      'nkl',
                      'filterExpressions',
                      'filterArguments',
                      'filterAlias',
                      'walkMeId',
                      'issueCode',
                      'flagAreaIdCode',
                      'importStep',
                      'portfolioId',
                      'placeholderId',
                      'accountMode',
                      'recsrc',
                      'trendPeriod',
                      'country',
                      'vertical',
                      'importType',
                      'importsrc',
                      'connectionId',
                      'assetGroupId',
                      'assetGroupTableMode',
                      'expertAccountPillSelected',
                      'editMode',
                      'adType',
                      'campaignType',
                      'adSubType',
                      'entrypoint',
                      'selectedCampaignObjectiveKey',
                      'checkmmcstore',
                      'brandListId',
                      'liftExperimentId',
                      'showTaxForm',
                      'isReAuth',
                      'templateGroupId',
                      'templateId',
                    ]}
                  >
                    <ServerContext.Provider value={this.props.serverContext}>
                      <UserContext.Provider value={this.userContext}>
                        <SettingsContext.Provider value={this.settingsContext}>
                          <PopupContextProvider permissions={this.permissions} isGateEnabled logger={this.logger}>
                            <ToastProvider>
                              <CampaignFetchProvider>
                              <DraftAccountPage>
                                <ScopeSwitchingReloader>
                                  <MultiStepTeachingBubbleContextProvider
                                    configs={teachingBubbleConfigs}
                                    logger={this.teachingBubbleLogger}
                                    serverContext={this.props.serverContext}
                                  >
                                    {isSDKView && (
                                    <React.Suspense fallback={null}>
                                      <LazySDKAppWrapper
                                        DelayedSubpageMenuContainer={DelayedSubpageMenuContainer}
                                        LayoutContainerWithRouter={LayoutContainerWithRouter}
                                      />
                                    </React.Suspense>
                                  )}
                                    {!isSDKView && (
                                    <AdsCopilotContextProvider>
                                      {displayMode === DisplayMode.Shellless && (
                                        <LayoutContainerWithRouter isGridLayout>
                                          <React.Suspense fallback={null}>
                                            <WebViewContextProvider>
                                              <main id="main">
                                                <Layout item="content" component={withAppPerfMarker(AppContent)} />
                                                <input type="hidden" id="internalErrorMsg" value={_TL_('We\'ve encountered an error and can\'t continue. Please contact support for further assistance.')} />
                                              </main>
                                              <Layout item="copilot" component={DelayedMSAdsCopilotLoader} />
                                            </WebViewContextProvider>
                                          </React.Suspense>
                                        </LayoutContainerWithRouter>
                                      )}
                                      {displayMode === DisplayMode.Default && (
                                        <LayoutContainerWithRouter isGridLayout={isNavRefresh}>
                                          <LayoutSwitch />
                                          <React.Suspense fallback={null}>
                                            <div className="nav-skip" role="navigation">
                                              <a href="#main">{i18n.getString(_TL_('Skip to main content'))}</a>
                                            </div>
                                            {!isNavRefresh && <Layout item="header" component={withAppPerfMarker(DelayedGlobalMenuWrapper)} onHamburgerClicked={this.onHamburgerClicked} />}
                                            {isNavRefresh && <Layout item="header" component={withAppPerfMarker(DelayedNewGlobalMenuWrapper)} />}
                                            {/* There is a race condition between the payment banner showing and the TablePageContainer loading.
                                                If the grid loads before the payment banner, the vertical scrollbar will be off by ~36 pixels.
                                                To address this, we recalculate the scrollbar position after the payment banner is shown. */}
                                            <Layout item="topBanner" component={DelayedTopBanner} />
                                            {!isNavRefresh && <Layout item="nav" component={withAppPerfMarker(DelayedMainMenu)} toggleMainMenuObservable={this.toggleMainMenuObservable} />}
                                            {isNavRefresh && <Layout item="nav" component={withAppPerfMarker(DelayedPrimaryNavigation)} customSiteMapProcessorSet={navRefreshProcessorSet} />}
                                            {isNavRefresh && <Layout item="scopePanel" component={withAppPerfMarker(DelayedScopePanel)} />}
                                            <Layout item="status" component={withAppPerfMarker(DelayedSummaryBarContainer)} />
                                            {!isNavRefresh && <Layout item="menu" component={withAppPerfMarker(DelayedPageMenuContainer)} />}
                                            {isNavRefresh && <Layout item="menu" component={withAppPerfMarker(DelayedSecondaryNavigation)} customSiteMapProcessorSet={navRefreshProcessorSet} />}
                                            <main id="main">
                                              <Layout item="tab" component={DelayedSubpageMenuContainer} />
                                              <Layout item="subTab" component={DelayedSubTabContainer} />
                                              <Layout item="content" component={withAppPerfMarker(AppContent)} />
                                              <input type="hidden" id="internalErrorMsg" value={_TL_('We\'ve encountered an error and can\'t continue. Please contact support for further assistance.')} />
                                            </main>
                                            <Layout item="copilot" component={DelayedMSAdsCopilotLoader} />
                                            <Layout item="overlay" component={OverlayComponent} />
                                          </React.Suspense>
                                        </LayoutContainerWithRouter>
                                      )}
                                    </AdsCopilotContextProvider>
                                  )}
                                    <React.Suspense fallback={null}>
                                      <DelayedTeachingBubbleRenderer aid={this.defaultScope.aid} cid={this.defaultScope.cid} permissions={this.permissions} isCouponEnabled={isCouponTeachingBubbleEnabled} />
                                    </React.Suspense>
                                  </MultiStepTeachingBubbleContextProvider>
                                </ScopeSwitchingReloader>
                                {shouldShowFallbackUpdater && <FallbackUpdaterWithDefaultLandingPage />}
                                <CampaignTypeUpdater />
                                <NewVersionReloader />
                                <WrappedGlobalContextReceiver />
                                <DelayedAfterPageReady />
                              </DraftAccountPage>
                              </CampaignFetchProvider>
                            </ToastProvider>
                          </PopupContextProvider>
                        </SettingsContext.Provider>
                      </UserContext.Provider>
                    </ServerContext.Provider>
                  </ScopeProvider>
                </PermissionsContext.Provider>
              </Router>
            </GlobalFilterContextProvider>
          </AppPerfMarker.Provider>
        </div>
      </App>
    );
  }
}

AppRoot.propTypes = {
  serverContext: PropTypes.shape({
    PageTrackingId: PropTypes.string.isRequired,
    CurrentLocale: PropTypes.string.isRequired,
    CurrentAccount: PropTypes.shape({
      AccountNumber: PropTypes.string,
    }),
    ServicesEndpoints: PropTypes.shape({
      ClientCenterUIAPI: PropTypes.string.isRequired,
    }),
  }).isRequired,
  instrumentation: PropTypes.shape().isRequired,
  appPerfMarker: perfMarkerPropTypes.isRequired,
  preferenceService: PropTypes.shape().isRequired,
  curTheme: PropTypes.string.isRequired,
};

export default AppRoot;
